// src/services/questionFactory/providers/geminiProvider.js
//
// Gemini (Google) generation provider. Live in-app generator.
// Talks to the shared question-factory-generate edge function which
// dispatches by `provider: 'gemini'` and holds GEMINI_API_KEY
// server-side as a Supabase secret.
//
// Output contract is the same canonical question JSON as ChatGPT and
// Claude. Gemini's JSON-mode (`responseMimeType: application/json`) is
// configured server-side so the model returns a clean JSON array of
// question objects that parseDraftsArray can consume directly.
//
// review() (multi-AI adjudication) wires up in Phase 2c.

import { supabase } from '../../supabaseClient';
import { ProviderError, normalizeGenerateOpts } from './providerBase';

const PROVIDER_ID   = 'gemini';
const DISPLAY_NAME  = 'Gemini';
const DEFAULT_MODEL = 'gemini-2.5-flash';

async function generate(opts = {}) {
  if (!opts.runId) {
    throw new ProviderError('generate(): runId is required (create a pipeline run first)', { provider: PROVIDER_ID });
  }
  const o = normalizeGenerateOpts(opts);

  const payload = {
    run_id:             opts.runId,
    provider:           PROVIDER_ID,
    exam_id:            o.examId,
    count:              o.count,
    domain:             o.domain,
    model:              opts.model || DEFAULT_MODEL,
    difficulty:         opts.difficulty || null,
    extra_instructions: opts.extraInstructions || null,
  };

  const { data, error } = await supabase.functions.invoke('question-factory-generate', { body: payload });
  if (error) {
    // FunctionsHttpError carries a Response we can drain for the server's reason.
    let detail = error.message || String(error);
    try {
      if (error.context && typeof error.context.text === 'function') {
        const txt = await error.context.text();
        if (txt) detail = `${detail} — ${txt}`;
      }
    } catch { /* swallow */ }
    throw new ProviderError(`Gemini generate failed: ${detail}`, { provider: PROVIDER_ID, cause: error });
  }
  if (!data)        throw new ProviderError('Gemini generate: empty response from edge function', { provider: PROVIDER_ID });
  if (data.error)   throw new ProviderError(`Gemini generate: ${data.error}`, { provider: PROVIDER_ID });

  return {
    inserted:        Number(data.items_inserted || 0),
    failed:          Number(data.items_failed   || 0),
    modelUsed:       data.model_used || DEFAULT_MODEL,
    inputTokens:     Number(data.input_tokens  || 0),
    outputTokens:    Number(data.output_tokens || 0),
    estimatedCostUsd: Number(data.estimated_cost_usd || 0),
    warnings:        Array.isArray(data.warnings) ? data.warnings : [],
    items:           Array.isArray(data.items) ? data.items : [],
  };
}

async function review(opts = {}) {
  if (!opts.question) {
    throw new ProviderError('review(): question is required', { provider: PROVIDER_ID });
  }
  throw new ProviderError('Gemini review() is implemented in Question Factory Phase 2c.', { provider: PROVIDER_ID });
}

const geminiProviderModule = {
  id:             PROVIDER_ID,
  displayName:    DISPLAY_NAME,
  enabled:        true,
  supportedModes: ['generate'],   // review() added in Phase 2c
  defaultModel:   DEFAULT_MODEL,
  generate,
  review,
};

export default geminiProviderModule;
